export async function recordingToWavFile(recording: Blob): Promise<File> {
  if (recording.type.split(';')[0].toLowerCase() === 'audio/wav') {
    return new File([recording], 'utterance.wav', { type: 'audio/wav' });
  }
  const AudioContextClass = window.AudioContext;
  if (!AudioContextClass || !window.OfflineAudioContext) {
    throw new Error('This browser cannot prepare the recording for pronunciation assessment.');
  }

  const context = new AudioContextClass();
  try {
    const decoded = await context.decodeAudioData(await recording.arrayBuffer());
    const sampleRate = 16_000;
    const frameCount = Math.max(1, Math.ceil(decoded.duration * sampleRate));
    const offline = new OfflineAudioContext(1, frameCount, sampleRate);
    const source = offline.createBufferSource();
    source.buffer = decoded;
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    return new File([encodePcmWav(rendered.getChannelData(0), sampleRate)], 'utterance.wav', { type: 'audio/wav' });
  } finally {
    await context.close();
  }
}

function encodePcmWav(samples: Float32Array, sampleRate: number) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(44 + index * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return buffer;
}

function writeAscii(view: DataView, offset: number, text: string) {
  for (let index = 0; index < text.length; index += 1) view.setUint8(offset + index, text.charCodeAt(index));
}

export async function blobToPeaks(blob: Blob, buckets = 64): Promise<number[]> {
  const AudioContextClass = window.AudioContext;
  if (!AudioContextClass) return [];
  const context = new AudioContextClass();
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    const channel = decoded.getChannelData(0);
    const size = Math.max(1, Math.floor(channel.length / buckets));
    const peaks: number[] = [];
    for (let bucket = 0; bucket < buckets; bucket += 1) {
      let peak = 0;
      const start = bucket * size;
      const end = Math.min(channel.length, start + size);
      for (let index = start; index < end; index += 1) peak = Math.max(peak, Math.abs(channel[index]));
      peaks.push(peak);
    }
    const highest = Math.max(...peaks, 0.0001);
    return peaks.map((value) => value / highest);
  } catch {
    return [];
  } finally {
    await context.close().catch(() => undefined);
  }
}
